import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, Plus, Clock, MapPin, Camera, X, Loader2, FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import MobileLayout from '../components/layout/MobileLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const severityColors = {
  low: 'bg-green-100 text-green-700 border-green-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  critical: 'bg-red-100 text-red-700 border-red-200',
};

const statusColors = {
  open: 'bg-amber-100 text-amber-700',
  investigating: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
};

export default function Incidents() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('report');
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const fileInputRef = useRef(null);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    severity: 'medium',
    location_description: '',
    photos: [],
  });

  const loadIncidents = useCallback(async () => {
    try {
      const data = await api.getIncidents();
      // Filter to show only user's incidents for guards
      const filtered = user?.role === 'guard' 
        ? data.filter(i => i.reported_by === user.id)
        : data;
      setIncidents(filtered);
    } catch (error) {
      console.error('Error loading incidents:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadIncidents();
  }, [loadIncidents]);

  const getLocation = async () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({});
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
        () => resolve({}),
        { timeout: 5000, enableHighAccuracy: true }
      );
    });
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setForm(prev => ({
          ...prev,
          photos: [...prev.photos, reader.result].slice(0, 5), // Max 5 photos
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) {
      alert('Please fill in title and description');
      return;
    }

    setSubmitting(true);
    try {
      const location = await getLocation();
      await api.createIncident({
        ...form,
        ...location,
      });
      setForm({
        title: '',
        description: '',
        severity: 'medium',
        location_description: '',
        photos: [],
      });
      loadIncidents();
      setActiveTab('my-reports');
      alert('Incident reported successfully!');
    } catch (error) {
      alert(error.message || 'Failed to report incident');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <div className="p-4" data-testid="incidents-page">
        <h1 className="text-2xl font-bold text-slate-800 mb-4">Incidents</h1>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="report" className="flex items-center gap-1">
              <Plus className="w-4 h-4" /> Report
            </TabsTrigger>
            <TabsTrigger value="my-reports" className="flex items-center gap-1">
              <FileText className="w-4 h-4" /> My Reports
            </TabsTrigger>
          </TabsList>

          <TabsContent value="report" className="mt-4">
            <Card>
              <CardContent className="p-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      placeholder="Brief incident title"
                      value={form.title}
                      onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                      data-testid="incident-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      placeholder="Describe what happened..."
                      value={form.description}
                      onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      data-testid="incident-description-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Severity</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {['low', 'medium', 'high', 'critical'].map((sev) => (
                        <button
                          key={sev}
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, severity: sev }))}
                          className={`py-2 px-3 rounded-lg text-sm font-medium border transition-all ${
                            form.severity === sev
                              ? severityColors[sev] + ' ring-2 ring-offset-1 ring-current'
                              : 'bg-slate-50 text-slate-600 border-slate-200'
                          }`}
                        >
                          {sev.charAt(0).toUpperCase() + sev.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Location (Optional)</Label>
                    <Input
                      placeholder="Where did this occur?"
                      value={form.location_description}
                      onChange={(e) => setForm(prev => ({ ...prev, location_description: e.target.value }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Photos (Optional)</Label>
                    <div className="flex flex-wrap gap-2">
                      {form.photos.map((photo, index) => (
                        <div key={index} className="relative w-20 h-20">
                          <img 
                            src={photo} 
                            alt={`Photo ${index + 1}`}
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {form.photos.length < 5 && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center hover:border-blue-400"
                        >
                          <Camera className="w-6 h-6 text-slate-400" />
                        </button>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoSelect}
                      className="hidden"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    data-testid="submit-incident-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Report Incident
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="my-reports" className="mt-4">
            <div className="space-y-3">
              {incidents.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-slate-500">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No incidents reported</p>
                  </CardContent>
                </Card>
              ) : (
                incidents.map((incident) => (
                  <Card key={incident.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-slate-800">{incident.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${severityColors[incident.severity]}`}>
                          {incident.severity}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{incident.description}</p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span className={`px-2 py-1 rounded-full ${statusColors[incident.status]}`}>
                          {incident.status}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {format(new Date(incident.created_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </MobileLayout>
  );
}